import React from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../../layouts';
import ModernInterviewLayout from './ModernInterviewLayout';
import ChatBot from './chatBot';
import { useAppSelector } from '../../stores';
import { FamlaServices } from '../../services';
import { clearAnalysisDraft } from '../../utils/analysisDraftStorage';
import { iInterview, iCollaboration } from '../../components/type'
import iMap from '../../components/type/iMap'
import { iAlert } from '../../interfaces'
import { Report } from '../../swimlane/lib/types'
import { typeProcess } from '../../components/type'
import {
    buildOwnerChecklistItems,
    buildCollaboratorChecklistItems,
    getGlobalProgress,
    getEffectiveCollaboratorMaxStepFromAnswers,
    getMaxStepFromAnswersForInterview,
    type ChecklistItem,
    type Step
} from '../../features/process/progress/processMappingProgress'
import { assignUniqueAvatarColors, getUserColor, getUserInitials } from '../../swimlane/hooks/useCollaboration'
import {
    ApprovalModal,
    GenericModal,
    GenericModalParent,
    GrantEditPermissionModal,
    InsufficientCreditsModal,
    InviteUser,
    ReplaceModal,
    ReminderModal,
    ShareModal,
} from '../modal';
import RoleFormModal from '../modal/RoleFormModal';
import ChangePersonRoleModal from '../modal/ChangePersonRoleModal';
import DeleteRoleModal from '../modal/DeleteRoleModal';
import EditModal from '../modal/editModal';
import Routes from '../../routes/Routes';
import { navigateToMapping, checkMappingOpenAccess } from '../../utils/mappingAccess';
import { showMappingLoadingOverlay, hideMappingLoadingOverlay, markInterviewDeferredMappingOpen, waitForOverlayPaint, clearInterviewDeferredMappingOpen } from '../../utils/mappingLoadingOverlay';
import { getProcessCreatePath } from '../../utils/processCreateRoute';
import { parseInsufficientCredits, parseMappingRegenConflict } from '../../utils/mappingDeleteConfirm';
import {
    getMappingGenCountdownSeconds,
    getMappingGenerationProgress,
    getSidebarDiagrams,
    hasActivePendingMappings,
    hasPendingMappings,
    isAwaitingFirstBatch,
    isMappingDiscoveryComplete,
    shouldShowMappingGenerationTimedOut,
} from '../../utils/mappingGenerationStatus';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService';
import {
    buildInviteRoleOptions,
    extractMentionedRolesFromAnswers,
    filterHiddenRoles,
    formatInviteRoleLabel,
    isProcessOwnerRoleLabel,
    normalizeInviteRoleKey,
    pinProcessOwnerRolesFirst,
    resolveRoleAlias,
} from '../../utils/inviteRoleOptions';

/** Aligné sur FORCE_GENERATION_COOLDOWN_MINUTES côté API — repli si le GET force-status renvoie 0 trop tôt */
const FORCE_MAP_COOLDOWN_FALLBACK_SEC = 300;

function extractDetectedRolesFromInterviews(roles: any[]): string[] {
    for (const r of roles) {
        const detected = r?.collaboration?.process?.detected_roles;
        if (Array.isArray(detected) && detected.length) {
            return pinProcessOwnerRolesFirst(
                detected.map((x: unknown) => String(x || '').trim()).filter(Boolean)
            );
        }
    }
    return [];
}

function extractRoleOrderFromInterviews(roles: any[]): string[] {
    for (const r of roles) {
        const order = r?.collaboration?.process?.role_order;
        if (Array.isArray(order) && order.length) {
            return pinProcessOwnerRolesFirst(order);
        }
    }
    return [];
}

function extractRoleAliasesFromInterviews(roles: any[]): Record<string, string> {
    for (const r of roles) {
        const aliases = r?.collaboration?.process?.role_aliases;
        if (aliases && typeof aliases === 'object' && !Array.isArray(aliases)) {
            return { ...aliases };
        }
    }
    return {};
}

function extractHiddenRolesFromInterviews(roles: any[]): string[] {
    for (const r of roles) {
        const hidden = r?.collaboration?.process?.hidden_roles;
        if (Array.isArray(hidden)) {
            return hidden.map((x: unknown) => String(x || '').trim()).filter(Boolean);
        }
    }
    return [];
}

/**
 * Page Interview modernisée qui utilise ModernInterviewLayout
 * Wrapper autour du ChatBot existant avec le nouveau design 3 colonnes
 */
const ModernInterviewView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roleParam = searchParams.get('role') || undefined;
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const role = useAppSelector((state) => state.role.role);
    const isAdmin = role === 'ADMIN' || role === 'admin';
    const isOwner = isAdmin;
    const closeRef = React.useRef<HTMLButtonElement>(null);
    const reminderCloseRef = React.useRef<HTMLButtonElement>(null);
    /** Envoie le « Oui » chat après succès d'invitation (évite d'avancer au 2e rôle pendant le modal). */
    const inviteYesSenderRef = React.useRef<(() => Promise<void>) | null>(null);
    
    const [processName, setProcessName] = React.useState(t('words.loading') || 'Loading...');

    const [interviewsData, setInterviewsData] = React.useState<iInterview[]>([]);
    const [currentInterview, setCurrentInterview] = React.useState<iInterview | null>(null);
    const [interviewwId, setInterviewwId] = React.useState<string | undefined>(id);
    const [swimlanes, setSwimlanes] = React.useState<any[]>([]);
    /** Cartos déjà confirmées accessibles — évite GET crédits avant navigation. */
    const accessibleMapIdsRef = React.useRef<Set<string>>(new Set());
    const [diagramsLoading, setDiagramsLoading] = React.useState(false);
    const [mappingGenNowMs, setMappingGenNowMs] = React.useState(() => Date.now());
    const [openingDiagramId, setOpeningDiagramId] = React.useState<string | null>(null);
    const [processId, setProcessId] = React.useState<string>('');
    const [time, setTime] = React.useState(0);
    const [processType, setProcessType] = React.useState<typeProcess>('AI');
    const [isSampleProcess, setIsSampleProcess] = React.useState(false);
    const [isProcessTypeResolved, setIsProcessTypeResolved] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [invited, setInvited] = React.useState<string | undefined>(roleParam);
    const [analyses, setAnalyses] = React.useState<Report[]>([]);
    const [ownerStep, setOwnerStep] = React.useState<Step | null>(null);
    const [liveOwnerStep, setLiveOwnerStep] = React.useState<Step | null>(null);
    const [collabStepsById, setCollabStepsById] = React.useState<Record<string, Step | null>>({});
    const [ownerEmail, setOwnerEmail] = React.useState<string | undefined>(undefined);
    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    });
    const [replace, setReplace] = React.useState<{ replaceId: string; replaceRole: string }>({
        replaceId: '',
        replaceRole: ''
    });
    const [contributor, setContributor] = React.useState(users.user);
    const [actionState, setActionState] = React.useState({
        actionId: '',
        swimlaneNameOrActionName: '',
        secondAction: '',
        interviewId: ''
    });
    const [forceGenStatus, setForceGenStatus] = React.useState({
        isProcessOwner: false,
        canForceGenerateMap: false,
        allInterviewsCompleted: false,
        canForceGenerate: false,
        cooldownRemainingSeconds: 0,
    });
    const [forceGenLoading, setForceGenLoading] = React.useState(false);
    const [showForceGenConfirm, setShowForceGenConfirm] = React.useState(false);
    const [documentMapBuildCountdown, setDocumentMapBuildCountdown] = React.useState<number | null>(null);
    const [mentionedRoles, setMentionedRoles] = React.useState<string[]>([]);
    const [detectedRolesFromProcess, setDetectedRolesFromProcess] = React.useState<string[]>([]);
    const [roleOrder, setRoleOrder] = React.useState<string[]>([]);
    const [roleAliases, setRoleAliases] = React.useState<Record<string, string>>({});
    const [hiddenRoles, setHiddenRoles] = React.useState<string[]>([]);
    const [invitePrefillRole, setInvitePrefillRole] = React.useState<string | undefined>(undefined);
    const [roleFormModal, setRoleFormModal] = React.useState<{
        mode: 'create' | 'rename';
        initialLabel?: string;
    } | null>(null);
    const [changeRoleModal, setChangeRoleModal] = React.useState<{
        memberId: string;
        currentRole: string;
        personName: string;
    } | null>(null);
    const [deleteRoleState, setDeleteRoleState] = React.useState<{
        label: string;
    } | null>(null);
    const [roleDeleteNeedsRegen, setRoleDeleteNeedsRegen] = React.useState<{
        estimatedCredits: number;
        creditsRemaining?: number;
    } | null>(null);
    const [mappingRegenConfirm, setMappingRegenConfirm] = React.useState<{
        interviewId: string;
        estimatedCredits: number;
        isOwnerInterview: boolean;
    } | null>(null);
    const [mappingRegenLoading, setMappingRegenLoading] = React.useState(false);
    const [liveInviteRoles, setLiveInviteRoles] = React.useState<string[]>([]);
    const [insufficientCreditsInfo, setInsufficientCreditsInfo] = React.useState<{
        creditsRequired: number;
        creditsAvailable: number;
        deficit: number;
        plan?: string;
    } | null>(null);

    const isLayoutReady = isProcessTypeResolved;
    const shouldShowMappingChecklist = isLayoutReady && processType === 'AI';

    const invitedCollaborations = React.useMemo(() => {
        return interviewsData.filter((i) => !i.for_owner);
    }, [interviewsData]);

    const ownerInterviewIdFromList = React.useMemo(() => {
        const owner = interviewsData.find((i) => i.for_owner);
        return owner?._id ? String(owner._id) : undefined;
    }, [interviewsData]);

    const isProcessOwner = React.useMemo(() => {
        if (isAdmin) return true;
        const myId = users.user?._id ? String(users.user._id) : '';
        if (!myId) return false;
        return interviewsData.some((i) => i.for_owner && String(i.collaboration?.user?._id ?? '') === myId);
    }, [isAdmin, users.user?._id, interviewsData]);

    /** Invitations / remplacement collaborateurs : admin entreprise ou propriétaire du processus */
    const canInviteOrReplaceCollaborators = isAdmin || isProcessOwner;

    /**
     * Ouverture d'une contribution dans la sidebar :
     * - process owner / admin : toutes
     * - invité : uniquement les siennes (1 ou N rôles = N interviews)
     */
    const canSelectContributorInterview = React.useMemo(() => {
        if (isProcessOwner) return true;
        const myId = users.user?._id ? String(users.user._id) : '';
        if (!myId) return false;
        return interviewsData.some(
            (i) => String(i.collaboration?.user?._id ?? '') === myId && Boolean(i._id)
        );
    }, [isProcessOwner, interviewsData, users.user?._id]);

    const handleSelectContributorInterview = React.useCallback(
        async (interviewMongoId: string) => {
            await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernSelectContributor);
            if (!interviewMongoId) return;
            if (isProcessOwner) {
                setInterviewwId(interviewMongoId);
                return;
            }
            const myId = users.user?._id ? String(users.user._id) : '';
            if (!myId) return;
            const target = interviewsData.find((i) => i._id && String(i._id) === String(interviewMongoId));
            if (target && String(target.collaboration?.user?._id ?? '') === myId) {
                setInterviewwId(interviewMongoId);
            }
        },
        [isProcessOwner, interviewsData, users.user?._id]
    );

    const collaboratorsForProgress = React.useMemo(() => {
        return invitedCollaborations.map((i) => {
            const rawId = i._id ?? null;
            const id = rawId ? String(rawId) : `pending:${i.collaboration?.user?.email ?? 'unknown'}`;
            const step = rawId ? collabStepsById[String(rawId)] ?? null : null;
            return { id, step };
        });
    }, [invitedCollaborations, collabStepsById]);

    const effectiveOwnerStep = React.useMemo(
        () => liveOwnerStep ?? ownerStep ?? null,
        [liveOwnerStep, ownerStep]
    );

    /**
     * « Ajouter contributeur » : dès l’étape invitation (1,5) et après
     * (phases suivantes / fin d’interview), pas seulement pendant (1,5).
     */
    const canAddContributor = React.useMemo(() => {
        if (!canInviteOrReplaceCollaborators) return false;
        if (!effectiveOwnerStep) return true;
        const [phase, sub] = effectiveOwnerStep;
        return phase > 1 || (phase === 1 && sub >= 5);
    }, [canInviteOrReplaceCollaborators, effectiveOwnerStep]);

    React.useEffect(() => {
        if (!id || id === interviewwId) return;
        setInterviewwId(id);
    }, [id]);

    React.useEffect(() => {
        const loadOwnerEmail = async () => {
            if (isAdmin || !users.session?.company_id || !users.access_token) return;
            try {
                const data = await FamlaServices("api_host", `/company/by/id/${users.session.company_id}`, "GET", "", users.access_token);
                if (data.status === 200 && data.body?.data?.owner?.email) {
                    setOwnerEmail(data.body.data.owner.email);
                }
            } catch (error) {
                // silent
            }
        };

        loadOwnerEmail();
    }, [isAdmin, users.session?.company_id, users.access_token]);

    React.useEffect(() => {
        if (insufficientCreditsInfo) {
            triggerModal('#insufficientCreditsModal');
        }
    }, [insufficientCreditsInfo]);

    const triggerModal = (selector: string) => {
        const target = document.querySelector(selector);
        if (!target) {
            console.warn('[ModernInterviewView] triggerModal: élément introuvable', selector);
            return;
        }
        const temp = document.createElement('button');
        temp.setAttribute('data-bs-target', selector);
        temp.setAttribute('data-bs-toggle', 'modal');
        document.body.appendChild(temp);
        temp.click();
        setTimeout(() => {
            if (temp.parentNode) document.body.removeChild(temp);
        }, 0);
    };

    const refreshProcessInterviews = React.useCallback(async () => {
        if (!processId) return;

        const enrichEditMode = async (roles: any[]) => {
            const getRoleAuthorization = async (roleId: string, pid: string): Promise<boolean> => {
                try {
                    if (!pid || !roleId) return false;
                    const data = await FamlaServices("api_host", `/role/${pid}/${roleId}`, "GET", "", users.access_token);
                    return data.body?.status_code === 200;
                } catch {
                    return false;
                }
            };
            const updatedRoles = await Promise.all(
                roles.map(async (role: any) => {
                    if (role.collaboration?.user) {
                        const editMode = await getRoleAuthorization(role.collaboration.user._id, processId);
                        return {
                            ...role,
                            collaboration: {
                                ...role.collaboration,
                                user: {
                                    ...role.collaboration.user,
                                    editMode,
                                },
                            },
                        };
                    }
                    return role;
                }),
            );
            setInterviewsData(updatedRoles);
        };

        try {
            const data = await FamlaServices("api_host", `/process/my/sub/${processId}`, "GET", "", users.access_token);
            const procesType = data.body?.data?.[0]?.collaboration?.process?.type;
            const sampleFlag = Boolean(data.body?.data?.[0]?.collaboration?.process?.is_sample);
            if (data.status === 200) {
                const roles = data.body.data;
                setProcessType((procesType || "AI") as typeProcess);
                setIsSampleProcess(sampleFlag);
                setInterviewsData(roles);
                setIsProcessTypeResolved(true);
                const order = extractRoleOrderFromInterviews(roles);
                if (order.length) setRoleOrder(order);
                const detected = extractDetectedRolesFromInterviews(roles);
                if (detected.length) setDetectedRolesFromProcess(detected);
                setRoleAliases(extractRoleAliasesFromInterviews(roles));
                setHiddenRoles(extractHiddenRolesFromInterviews(roles));
                void enrichEditMode(roles);
            } else {
                setIsProcessTypeResolved(true);
            }
        } catch {
            // silent
        } finally {
            setIsProcessTypeResolved(true);
        }
    }, [processId, users.access_token]);

    const showRoleCrudError = React.useCallback((message: string) => {
        setAlert({ isActive: true, message, type: 'warning' });
        setTimeout(() => {
            setAlert((prev) => ({ ...prev, isActive: false, message: '' }));
        }, 5000);
    }, []);

    const processOwnerDisplayLabel = (
        t('features.swimlane.processOwner') || 'Process Owner'
    ).trim();

    const syncRoleOrder = React.useCallback(
        (order: string[] | ((prev: string[]) => string[])) => {
            if (typeof order === 'function') {
                setRoleOrder((prev) =>
                    pinProcessOwnerRolesFirst(order(prev), processOwnerDisplayLabel)
                );
            } else {
                setRoleOrder(pinProcessOwnerRolesFirst(order, processOwnerDisplayLabel));
            }
        },
        [processOwnerDisplayLabel]
    );

    const syncRoleMetaFromResponse = React.useCallback(
        (data: any) => {
            const order = data?.role_order;
            if (Array.isArray(order)) syncRoleOrder(order);
            const detected = data?.detected_roles;
            if (Array.isArray(detected)) {
                setDetectedRolesFromProcess(
                    pinProcessOwnerRolesFirst(
                        detected.map((x: unknown) => String(x || '').trim()).filter(Boolean)
                    )
                );
            }
            const aliases = data?.role_aliases;
            if (aliases && typeof aliases === 'object' && !Array.isArray(aliases)) {
                setRoleAliases({ ...aliases });
            }
            if (Array.isArray(data?.hidden_roles)) {
                setHiddenRoles(
                    data.hidden_roles.map((x: unknown) => String(x || '').trim()).filter(Boolean)
                );
            }
        },
        [syncRoleOrder]
    );

    /** Map UI display label (formatted/translated) back to the stored Interview.role string. */
    const resolveStoredRoleLabel = React.useCallback(
        (label: string): string => {
            const needle = label.trim().toLowerCase();
            if (!needle) return label.trim();

            const contributorLabel = (t('views.processInterview.contributor') || 'Contributor').trim();
            const processOwnerLabel = (t('features.swimlane.processOwner') || 'Process Owner').trim();

            const displayOf = (raw?: string | null): string => {
                const trimmed = (raw || '').trim();
                if (!trimmed) return contributorLabel;
                const normalized = trimmed.toUpperCase();
                if (normalized === 'OWNER' || normalized === 'ADMIN') return processOwnerLabel;
                if (normalized === 'WORKER') return contributorLabel;
                return trimmed
                    .split(' ')
                    .map((word) =>
                        word
                            .split('-')
                            .map((segment) => {
                                if (!segment) return segment;
                                if (segment.toUpperCase() === segment && segment.length <= 3) {
                                    return segment;
                                }
                                return `${segment[0].toUpperCase()}${segment.slice(1).toLowerCase()}`;
                            })
                            .join('-')
                    )
                    .join(' ');
            };

            for (const iv of interviewsData) {
                const raw = (iv.role || '').trim();
                if (!raw) continue;
                if (raw.toLowerCase() === needle) return raw;
                if (displayOf(raw).toLowerCase() === needle) return raw;
            }
            for (const r of roleOrder) {
                if (r.trim().toLowerCase() === needle) return r.trim();
            }
            for (const r of mentionedRoles) {
                if (r.trim().toLowerCase() === needle) return r.trim();
                if (displayOf(r).toLowerCase() === needle) return r.trim();
            }
            return label.trim();
        },
        [interviewsData, roleOrder, mentionedRoles, t]
    );

    const handleRenameRole = React.useCallback(
        async (from: string, to: string) => {
            const toNorm = to.trim();
            const fromNorm = resolveStoredRoleLabel(from);
            if (!processId || !fromNorm || !toNorm || fromNorm === toNorm) return true;
            if (['OWNER', 'ADMIN'].includes(fromNorm.toUpperCase())) {
                showRoleCrudError(
                    t('views.processInterview.roleUpdateFailed', 'Could not update role.')
                );
                return false;
            }

            const existing = new Set(
                [
                    ...roleOrder,
                    ...interviewsData.map((i) => i.role || ''),
                    ...mentionedRoles,
                ]
                    .map((r) => r.trim().toLowerCase())
                    .filter(Boolean)
            );
            if (toNorm.toLowerCase() !== fromNorm.toLowerCase() && existing.has(toNorm.toLowerCase())) {
                showRoleCrudError(t('views.processInterview.duplicateRoleName', 'This role already exists.'));
                return false;
            }

            const prevInterviews = interviewsData;
            const prevMentioned = mentionedRoles;
            const prevOrder = roleOrder;
            const prevAliases = roleAliases;

            setInterviewsData((prev) =>
                prev.map((iv) =>
                    (iv.role || '').trim().toLowerCase() === fromNorm.toLowerCase()
                        ? { ...iv, role: toNorm }
                        : iv
                )
            );
            setMentionedRoles((prev) =>
                prev.map((r) => (r.trim().toLowerCase() === fromNorm.toLowerCase() ? toNorm : r))
            );
            setRoleOrder((prev) => {
                const next = prev.map((r) =>
                    r.trim().toLowerCase() === fromNorm.toLowerCase() ? toNorm : r
                );
                if (!next.some((r) => r.trim().toLowerCase() === toNorm.toLowerCase())) {
                    next.push(toNorm);
                }
                return pinProcessOwnerRolesFirst(next, processOwnerDisplayLabel);
            });
            setRoleAliases((prev) => {
                const next = { ...prev };
                const fromKey = normalizeInviteRoleKey(fromNorm);
                const toKey = normalizeInviteRoleKey(toNorm);
                for (const [k, v] of Object.entries(next)) {
                    if (normalizeInviteRoleKey(v) === fromKey) next[k] = toNorm;
                }
                if (fromKey && toKey && fromKey !== toKey) next[fromKey] = toNorm;
                if (toKey) delete next[toKey];
                return next;
            });

            try {
                const data = await FamlaServices(
                    'api_host',
                    `/process/${processId}/roles`,
                    'PATCH',
                    { action: 'rename', from: fromNorm, to: toNorm },
                    users.access_token
                );
                if (data.body?.status_code === 409 || data.status === 409) {
                    setInterviewsData(prevInterviews);
                    setMentionedRoles(prevMentioned);
                    setRoleOrder(prevOrder);
                    setRoleAliases(prevAliases);
                    showRoleCrudError(t('views.processInterview.duplicateRoleName', 'This role already exists.'));
                    return false;
                }
                if (data.body?.status_code !== 200 && data.status !== 200) {
                    setInterviewsData(prevInterviews);
                    setMentionedRoles(prevMentioned);
                    setRoleOrder(prevOrder);
                    setRoleAliases(prevAliases);
                    showRoleCrudError(data.body?.description || t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
                    return false;
                }
                syncRoleMetaFromResponse(data.body?.data);
                return true;
            } catch {
                setInterviewsData(prevInterviews);
                setMentionedRoles(prevMentioned);
                setRoleOrder(prevOrder);
                setRoleAliases(prevAliases);
                showRoleCrudError(t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
                return false;
            }
        },
        [processId, roleOrder, interviewsData, mentionedRoles, roleAliases, users.access_token, showRoleCrudError, t, resolveStoredRoleLabel, processOwnerDisplayLabel, syncRoleMetaFromResponse]
    );

    const handleCreateRole = React.useCallback(
        async (label: string) => {
            const toNorm = label.trim();
            if (!processId || !toNorm) return false;
            const existing = new Set(
                [...roleOrder, ...interviewsData.map((i) => i.role || ''), ...mentionedRoles]
                    .map((r) => r.trim().toLowerCase())
                    .filter(Boolean)
            );
            if (existing.has(toNorm.toLowerCase())) {
                showRoleCrudError(t('views.processInterview.duplicateRoleName', 'This role already exists.'));
                return false;
            }
            const prevOrder = roleOrder;
            const prevHidden = hiddenRoles;
            syncRoleOrder((prev) => [...prev, toNorm]);
            setHiddenRoles((prev) =>
                prev.filter((r) => normalizeInviteRoleKey(r) !== normalizeInviteRoleKey(toNorm))
            );
            try {
                const data = await FamlaServices(
                    'api_host',
                    `/process/${processId}/roles`,
                    'PATCH',
                    { action: 'create', label: toNorm },
                    users.access_token
                );
                if (data.body?.status_code === 409 || data.status === 409) {
                    setRoleOrder(prevOrder);
                    setHiddenRoles(prevHidden);
                    showRoleCrudError(t('views.processInterview.duplicateRoleName', 'This role already exists.'));
                    return false;
                }
                if (data.body?.status_code !== 200 && data.status !== 200) {
                    setRoleOrder(prevOrder);
                    setHiddenRoles(prevHidden);
                    showRoleCrudError(data.body?.description || t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
                    return false;
                }
                syncRoleMetaFromResponse(data.body?.data);
                return true;
            } catch {
                setRoleOrder(prevOrder);
                setHiddenRoles(prevHidden);
                showRoleCrudError(t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
                return false;
            }
        },
        [processId, roleOrder, interviewsData, mentionedRoles, hiddenRoles, users.access_token, showRoleCrudError, t, syncRoleOrder, syncRoleMetaFromResponse]
    );

    const handleReorderRoles = React.useCallback(
        async (orderedLabels: string[]) => {
            if (!processId) return;
            const prevOrder = roleOrder;
            // Persist métier roles only — Process Owner stays pinned first in the UI
            const next = pinProcessOwnerRolesFirst(
                orderedLabels.filter(
                    (r) => !isProcessOwnerRoleLabel(r, processOwnerDisplayLabel)
                ),
                processOwnerDisplayLabel
            );
            setRoleOrder(next);
            try {
                const data = await FamlaServices(
                    'api_host',
                    `/process/${processId}/role-order`,
                    'PUT',
                    { roles: next },
                    users.access_token
                );
                if (data.body?.status_code !== 200 && data.status !== 200) {
                    setRoleOrder(prevOrder);
                    showRoleCrudError(data.body?.description || t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
                }
            } catch {
                setRoleOrder(prevOrder);
                showRoleCrudError(t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
            }
        },
        [processId, roleOrder, users.access_token, showRoleCrudError, t, processOwnerDisplayLabel]
    );

    const handleMovePerson = React.useCallback(
        async (memberId: string, toRole: string) => {
            if (!processId || !toRole.trim()) return;
            const toNorm = resolveStoredRoleLabel(toRole);
            const prevInterviews = interviewsData;
            const targetInterview = interviewsData.find((iv) => {
                const mongo = iv._id ? String(iv._id) : '';
                const token = (iv as any).invitation_token_id
                    ? String((iv as any).invitation_token_id)
                    : '';
                return mongo === memberId || token === memberId || `pending-${token}` === memberId;
            });

            let apiId = memberId;
            if (targetInterview) {
                if (targetInterview._id) {
                    apiId = String(targetInterview._id);
                    setInterviewsData((prev) =>
                        prev.map((iv) =>
                            iv._id && String(iv._id) === apiId ? { ...iv, role: toNorm } : iv
                        )
                    );
                } else if ((targetInterview as any).invitation_token_id) {
                    const token = String((targetInterview as any).invitation_token_id);
                    apiId = `pending-${token}`;
                    setInterviewsData((prev) =>
                        prev.map((iv) =>
                            String((iv as any).invitation_token_id || '') === token
                                ? { ...iv, role: toNorm }
                                : iv
                        )
                    );
                }
            } else if (memberId.startsWith('mentioned-')) {
                // placeholders have no assignment to move
                return;
            }

            try {
                const data = await FamlaServices(
                    'api_host',
                    `/interview/${apiId}/role`,
                    'PATCH',
                    { role: toNorm, process_id: processId },
                    users.access_token
                );
                if (data.body?.status_code !== 200 && data.status !== 200) {
                    setInterviewsData(prevInterviews);
                    showRoleCrudError(
                        data.body?.description ||
                            t('views.processInterview.moveFailed', 'Could not move contributor.')
                    );
                    return;
                }
                syncRoleOrder((prev) => [...prev, toNorm]);
            } catch {
                setInterviewsData(prevInterviews);
                showRoleCrudError(t('views.processInterview.moveFailed', 'Could not move contributor.'));
            }
        },
        [processId, interviewsData, users.access_token, showRoleCrudError, t, resolveStoredRoleLabel, syncRoleOrder]
    );

    const openDeleteRoleModal = React.useCallback((label: string) => {
        const stored = resolveStoredRoleLabel(label);
        if (['OWNER', 'ADMIN'].includes(stored.toUpperCase())) {
            showRoleCrudError(
                t('views.processInterview.roleUpdateFailed', 'Could not update role.')
            );
            return;
        }
        setDeleteRoleState({ label: stored });
        setRoleDeleteNeedsRegen(null);
    }, [resolveStoredRoleLabel, showRoleCrudError, t]);

    const deleteRoleHasAssignees = React.useMemo(() => {
        const label = deleteRoleState?.label;
        if (!label) return false;
        const key = normalizeInviteRoleKey(label);
        if (!key) return false;
        return interviewsData.some((iv) => {
            if (iv.for_owner) return false;
            const roleKey = normalizeInviteRoleKey(
                iv.role || iv.collaboration?.worker_role || ''
            );
            return roleKey === key;
        });
    }, [deleteRoleState?.label, interviewsData]);

    const deleteRoleLabelRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (!deleteRoleState) deleteRoleLabelRef.current = null;
    }, [deleteRoleState]);

    const confirmDeleteRole = React.useCallback(
        async (payload: { strategy: 'reassign' | 'remove'; targetRole?: string }) => {
        if (!processId || !deleteRoleState) return false;
        const label = deleteRoleState.label;
        const strategy = payload.strategy;
        const targetRole = payload.targetRole || '';
        const prevInterviews = interviewsData;
        const prevMentioned = mentionedRoles;
        const prevOrder = roleOrder;
        const prevHidden = hiddenRoles;
        const labelKey = label.trim().toLowerCase();
        const storedLabel = resolveStoredRoleLabel(label);

        // Masquer immédiatement (évite le retour via liveInviteRoles / reload answers)
        setHiddenRoles((prev) => {
            const key = normalizeInviteRoleKey(storedLabel);
            if (!key || prev.some((r) => normalizeInviteRoleKey(r) === key)) return prev;
            return [...prev, storedLabel];
        });

        if (strategy === 'reassign') {
            const target = resolveStoredRoleLabel(targetRole.trim());
            if (!target || target.toLowerCase() === labelKey) {
                setHiddenRoles(prevHidden);
                showRoleCrudError(
                    t('views.processInterview.reassignTargetRequired', 'Please choose a target role.')
                );
                return false;
            }
            setInterviewsData((prev) =>
                prev.map((iv) =>
                    (iv.role || '').trim().toLowerCase() === labelKey ? { ...iv, role: target } : iv
                )
            );
            setMentionedRoles((prev) =>
                prev
                    .map((r) => (r.trim().toLowerCase() === labelKey ? target : r))
                    .filter((r) => normalizeInviteRoleKey(r) !== normalizeInviteRoleKey(storedLabel))
            );
            syncRoleOrder((prev) =>
                prev.filter((r) => r.trim().toLowerCase() !== labelKey).concat(target)
            );
        } else {
            setInterviewsData((prev) =>
                prev.filter(
                    (iv) =>
                        iv.for_owner ||
                        (iv.role || '').trim().toLowerCase() !== labelKey
                )
            );
            setMentionedRoles((prev) => prev.filter((r) => r.trim().toLowerCase() !== labelKey));
            syncRoleOrder((prev) => prev.filter((r) => r.trim().toLowerCase() !== labelKey));
        }

        try {
            const data = await FamlaServices(
                'api_host',
                `/process/${processId}/roles`,
                'PATCH',
                {
                    action: 'delete',
                    label: storedLabel,
                    strategy,
                    target_role:
                        strategy === 'reassign'
                            ? resolveStoredRoleLabel(targetRole.trim())
                            : undefined,
                    confirm_regenerate:
                        strategy === 'remove' && Boolean(roleDeleteNeedsRegen),
                },
                users.access_token
            );
            if (data.body?.status_code !== 200 && data.status !== 200) {
                setInterviewsData(prevInterviews);
                setMentionedRoles(prevMentioned);
                setRoleOrder(prevOrder);
                setHiddenRoles(prevHidden);
                const regen = parseMappingRegenConflict(data);
                if (regen && strategy === 'remove') {
                    setRoleDeleteNeedsRegen(regen);
                    return false;
                }
                const creditsDenied = parseInsufficientCredits(data);
                if (creditsDenied) {
                    setInsufficientCreditsInfo(creditsDenied);
                    return false;
                }
                showRoleCrudError(
                    data.body?.description ||
                        t('views.processInterview.roleUpdateFailed', 'Could not update role.')
                );
                return false;
            }
            syncRoleMetaFromResponse(data.body?.data);
            setRoleDeleteNeedsRegen(null);
            setDeleteRoleState(null);
            return true;
        } catch {
            setInterviewsData(prevInterviews);
            setMentionedRoles(prevMentioned);
            setRoleOrder(prevOrder);
            setHiddenRoles(prevHidden);
            showRoleCrudError(t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
            return false;
        }
    }, [
        processId,
        deleteRoleState,
        interviewsData,
        mentionedRoles,
        roleOrder,
        hiddenRoles,
        users.access_token,
        showRoleCrudError,
        t,
        resolveStoredRoleLabel,
        syncRoleOrder,
        syncRoleMetaFromResponse,
        roleDeleteNeedsRegen,
    ]);

    const handleReplaceMenber = React.useCallback(
        async (replaceRole: string, replaceId: string, action: string) => {
            if (!canInviteOrReplaceCollaborators) return;
            if (action === 'replace') {
                await trackUiButtonEventAsync(
                    UI_BUTTON_EVENTS.interview.modernCollaboratorReplace
                );
            } else {
                await trackUiButtonEventAsync(
                    UI_BUTTON_EVENTS.interview.modernCollaboratorReminder
                );
            }
            setReplace({ replaceId, replaceRole });
            setTimeout(() => {
                if (action === 'replace') {
                    triggerModal('#replaceModal');
                } else {
                    triggerModal('#reminderModal');
                }
            }, 0);
        },
        [canInviteOrReplaceCollaborators]
    );

    const extractMappingsFromResponse = React.useCallback((data: any): any[] => {
        if (data?.status === 200) {
            const responseData = data.body?.data;
            return Array.isArray(responseData) ? responseData : responseData?.accessible || [];
        }
        if (data?.status === 402) {
            const response = data.body?.data;
            let allMappings: any[] = [];
            if (response?.accessible?.length > 0) {
                allMappings = [...response.accessible];
            }
            if (response?.locked?.mappings?.length > 0) {
                allMappings = [...allMappings, ...response.locked.mappings];
            }
            return allMappings;
        }
        return [];
    }, []);

    const mergeSwimlaneMeta = React.useCallback((incoming: any[]) => {
        const next = [...incoming].reverse();
        setSwimlanes((prev) => {
            if (prev.length === 0) return next;
            const prevById = new Map(prev.map((item) => [String(item._id || item.id), item]));
            return next.map((item) => {
                const id = String(item._id || item.id);
                const existing = prevById.get(id);
                return existing ? { ...existing, ...item } : item;
            });
        });
    }, []);

    const loadAllSwimlane = React.useCallback(async (pid: string) => {
        if (!pid) return;
        setDiagramsLoading(true);
        try {
            const [data, stats] = await Promise.all([
                FamlaServices(
                    "api_host",
                    `/mapping/process/${pid}?fields=meta&skip_enrich=true`,
                    "GET",
                    "",
                    users.access_token
                ),
                FamlaServices("api_host", `/stats/process/${pid}`, "GET", "", users.access_token).catch(() => null),
            ]);
                if (data.status === 200) {
                    const responseData = data.body.data;
                    const datas = Array.isArray(responseData) ? responseData : responseData.accessible || [];
                const merged = datas.reverse();
                setSwimlanes(merged);
                accessibleMapIdsRef.current = new Set(
                    datas
                        .map((item: { _id?: string; id?: string }) => String(item._id || item.id || ''))
                        .filter(Boolean)
                );
                } else if (data.status === 402) {
                    const response = data.body.data;
                    let allMappings: any[] = [];
                    if (response.accessible && response.accessible.length > 0) {
                        allMappings = [...response.accessible];
                    }
                    if (response.locked && response.locked.mappings && response.locked.mappings.length > 0) {
                        allMappings = [...allMappings, ...response.locked.mappings];
                    }
                const merged = allMappings.reverse();
                setSwimlanes(merged);
                accessibleMapIdsRef.current = new Set(
                    (response.accessible || [])
                        .map((item: { _id?: string; id?: string }) => String(item._id || item.id || ''))
                        .filter(Boolean)
                );
            } else {
                accessibleMapIdsRef.current = new Set();
            }
            if (stats?.status === 200) {
                setTime(stats.body.data);
            }
        } catch {
                // silent
        } finally {
            setDiagramsLoading(false);
        }
    }, [users.access_token, extractMappingsFromResponse]);

    const refreshSwimlaneMeta = React.useCallback(async (pid: string) => {
        if (!pid) return;
        try {
            const data = await FamlaServices(
                "api_host",
                `/mapping/process/${pid}?fields=meta`,
                "GET",
                "",
                users.access_token,
            );
            const mappings = extractMappingsFromResponse(data);
            if (mappings.length > 0 || data?.status === 200 || data?.status === 402) {
                mergeSwimlaneMeta(mappings);
            }
        } catch {
            // silent
        }
    }, [users.access_token, extractMappingsFromResponse, mergeSwimlaneMeta]);

    React.useEffect(() => {
        const updateNotificationProcess = async (resolvedProcessId: string) => {
            if (resolvedProcessId) {
                FamlaServices("api_host", `/notification/${resolvedProcessId}`, "PUT", "", users.access_token);
            }
        };

        let cancelled = false;

        const getInterviewById = async () => {
            const lookupId = id;
            if (!lookupId) return;

            const invitedUserEmail = sessionStorage.getItem('invited_user_email');
            const isInvitedUser = invitedUserEmail &&
                users.session?.user?.email?.toLowerCase() === invitedUserEmail.toLowerCase() &&
                users.session?.user?.fullname;

            let data: any = null;
            let isProcessId = false;

            if (isInvitedUser) {
                data = await FamlaServices("api_host", `/interview/process/${lookupId}`, "GET", "", users.access_token);
                if (data.status === 200) {
                    isProcessId = true;
                } else if (data.status === 404) {
                    try {
                        const roleCheck = await FamlaServices("api_host", `/process/my/role/${lookupId}`, "GET", "", users.access_token);
                        if (roleCheck.status === 200 || roleCheck.status === 404) {
                            data = await FamlaServices("api_host", `/interview/process/${lookupId}/owner`, "GET", "", users.access_token);
                            if (data.status === 200) {
                                isProcessId = true;
                            }
                        }
                    } catch (error) {
                        data = await FamlaServices("api_host", `/interview/process/${lookupId}/owner`, "GET", "", users.access_token);
                        if (data.status === 200) {
                            isProcessId = true;
                        }
                    }
                }
            } else {
                if (invitedUserEmail && (!users.session?.user?.fullname || users.session?.user?.email?.toLowerCase() !== invitedUserEmail.toLowerCase())) {
                    data = await FamlaServices("api_host", `/interview/process/${lookupId}/owner`, "GET", "", users.access_token);
                    if (data.status === 200) {
                        isProcessId = true;
                    }
                } else {
                    data = await FamlaServices("api_host", `/interview/process/${lookupId}`, "GET", "", users.access_token);
                    if (data.status === 200) {
                        isProcessId = true;
                    } else if (data.status === 404) {
                        data = await FamlaServices("api_host", `/interview/by/id/${lookupId}`, "GET", "", users.access_token);
                        if (data.status === 404) {
                            if (cancelled) return;
                            setLoading(false);
                            navigate(Routes.HOMEVIEW);
                            return;
                        }
                    }
                }
            }

            if (cancelled) return;

            if (data && data.status === 200) {
                const interviewData = Array.isArray(data.body.data) ? data.body.data[0] : data.body.data;
                if (isProcessId && interviewData?._id) {
                    setInterviewwId(interviewData._id);
                    setActionState((prev) => ({ ...prev, interviewId: interviewData._id }));
                }
                setCurrentInterview(interviewData);

                const resolvedProcessId = interviewData?.collaboration?.process?._id;
                const owner = interviewData?.collaboration?.process?.owner;

                if (resolvedProcessId) {
                    setProcessId(resolvedProcessId);
                    setIsSampleProcess(Boolean(interviewData?.collaboration?.process?.is_sample));
                    updateNotificationProcess(resolvedProcessId);
                    loadAllSwimlane(resolvedProcessId);

                    const processName = interviewData?.collaboration?.process?.title || 
                                      interviewData?.collaboration?.process?.name || 
                                      interviewData?.title ||
                                      interviewData?.name ||
                                      owner?.fullname ||
                                      'Process Interview';
                    
                    setProcessName(processName);
                }
            } else if (data && data.status === 404) {
                if (cancelled) return;
                setLoading(false);
                navigate(Routes.HOMEVIEW);
            }

            if (cancelled) return;
            setLoading(false);
        };

        void getInterviewById();

        return () => {
            cancelled = true;
        };
    }, [id, users.access_token, navigate, users.session?.user?.email, users.session?.user?.fullname, loadAllSwimlane]);

    const loadAllAnalyses = React.useCallback(async () => {
        if (!processId) return;
        try {
            const data = await FamlaServices("api_host", `/report/saved/${processId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                setAnalyses(data.body.data || []);
            }
        } catch (error) {
            // silent
        }
    }, [processId, users.access_token]);

    React.useEffect(() => {
        // Aligner la checklist collaborateur sur GET /answer (max step) — pas réservé au owner :
        // un contributeur connecté sur cette vue n’a pas sinon de currentStep (collabStepsById vide).
        if (!shouldShowMappingChecklist) return;
        if (!processId || !users.access_token) return;

        let cancelled = false;
        let intervalId: number | undefined;

        const fetchMaxStepForInterview = async (
            interviewId: string,
            mode: "owner" | "collaborator"
        ): Promise<Step | null> => {
            try {
                const data = await FamlaServices("api_host", `/answer/${interviewId}`, "GET", "", users.access_token);
                if (data?.status !== 200) return null;
                const responseData = data.body?.data;
                const answers = responseData?.answers ?? responseData;
                const arr = Array.isArray(answers) ? answers : [];
                if (mode === "owner") {
                    return getMaxStepFromAnswersForInterview(arr, interviewId);
                }
                return getEffectiveCollaboratorMaxStepFromAnswers(arr);
            } catch (e) {
                return null;
            }
        };

        const resolveOwnerInterviewId = async (): Promise<string | undefined> => {
            if (ownerInterviewIdFromList) return ownerInterviewIdFromList;
            try {
                const data = await FamlaServices("api_host", `/interview/process/${processId}/owner`, "GET", "", users.access_token);
                if (data?.status !== 200) return undefined;
                const bodyData = data.body?.data;
                const interviewData = Array.isArray(bodyData) ? bodyData?.[0] : bodyData;
                const ownerId = interviewData?._id;
                return ownerId ? String(ownerId) : undefined;
            } catch (e) {
                return undefined;
            }
        };

        const fetchSnapshot = async () => {
            if (cancelled) return;
            const collaboratorIds = Array.from(
                new Set(
                    invitedCollaborations
                        .map((i) => (i._id ? String(i._id) : null))
                        .filter((x): x is string => Boolean(x))
                )
            );

            const ownerInterviewId = await resolveOwnerInterviewId();

            const [ownerStepFromApi, collaboratorSteps] = await Promise.all([
                ownerInterviewId ? fetchMaxStepForInterview(ownerInterviewId, "owner") : Promise.resolve(null),
                Promise.all(
                    collaboratorIds.map(async (cid) => ({
                        id: cid,
                        step: await fetchMaxStepForInterview(cid, "collaborator"),
                    }))
                ),
            ]);

            if (cancelled) return;

            setOwnerStep(ownerStepFromApi);
            setCollabStepsById(() => {
                const next: Record<string, Step | null> = {};
                for (const c of collaboratorSteps) {
                    next[c.id] = c.step;
                }
                return next;
            });
        };

        void fetchSnapshot();
        intervalId = window.setInterval(fetchSnapshot, 8000);

        return () => {
            cancelled = true;
            if (intervalId) window.clearInterval(intervalId);
        };
    }, [shouldShowMappingChecklist, users.access_token, processId, invitedCollaborations, ownerInterviewIdFromList]);

    React.useEffect(() => {
        const handleFocus = () => {
            loadAllAnalyses();
        };

        const handleAnalysisUpdated = () => {
            loadAllAnalyses();
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('analysisListUpdated', handleAnalysisUpdated);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('analysisListUpdated', handleAnalysisUpdated);
        };
    }, [loadAllAnalyses]);

    // Use the same color generation function as swimlane diagrams for consistency
    const getColorForUser = (seed: string): string => {
        return getUserColor(seed);
    };

    const normalizeRoleKey = React.useCallback((role?: string | null): string => {
        return normalizeInviteRoleKey(role || '');
    }, []);

    const formatRoleLabel = React.useCallback((value: string): string => {
        return formatInviteRoleLabel(value) || value;
    }, []);

    const loadMentionedRoles = React.useCallback(async () => {
        if (!processId || !users.access_token) {
            setMentionedRoles([]);
            return [] as string[];
        }
        try {
            let ownerInterviewId = ownerInterviewIdFromList;
            if (!ownerInterviewId) {
            const ownerResponse = await FamlaServices(
                'api_host',
                `/interview/process/${processId}/owner`,
                'GET',
                '',
                users.access_token
            );
            if (ownerResponse.status !== 200) return [] as string[];

            const ownerData = ownerResponse.body?.data;
            const ownerInterview = Array.isArray(ownerData) ? ownerData?.[0] : ownerData;
                ownerInterviewId = ownerInterview?._id ? String(ownerInterview._id) : '';
            }
            if (!ownerInterviewId) return [] as string[];

            const answersResponse = await FamlaServices(
                'api_host',
                `/answer/${ownerInterviewId}`,
                'GET',
                '',
                users.access_token
            );
            if (answersResponse.status !== 200) return [] as string[];

            const responseData = answersResponse.body?.data;
            const answers = responseData?.answers ?? responseData;
            if (!Array.isArray(answers)) return [] as string[];

            const roles = filterHiddenRoles(
                extractMentionedRolesFromAnswers(answers),
                hiddenRoles,
                roleAliases
            );
            flushSync(() => {
                setMentionedRoles(roles);
            });
            return roles;
        } catch (error) {
            return [] as string[];
        }
    }, [processId, users.access_token, roleAliases, hiddenRoles, ownerInterviewIdFromList]);

    /** Chargement initial : membres en priorité, insights différés (secondaire). */
    React.useEffect(() => {
        if (!processId) return;
        void refreshProcessInterviews();
        const loadInsights = () => void loadAllAnalyses();
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(loadInsights, { timeout: 2000 });
        } else {
            window.setTimeout(loadInsights, 150);
        }
    }, [processId, refreshProcessInterviews, loadAllAnalyses]);

    /** Repli legacy si process.detected_roles pas encore rempli (processus anciens) */
    React.useEffect(() => {
        if (!processId || detectedRolesFromProcess.length) return;
            void loadMentionedRoles();
    }, [processId, detectedRolesFromProcess.length, loadMentionedRoles]);

    /** Refresh sidebar uniquement aux steps qui modifient les rôles (pas chaque message phase 1) */
    React.useEffect(() => {
        if (!effectiveOwnerStep) return;
                      const [phase, sub] = effectiveOwnerStep;
        const roleMutationSteps = new Set([2, 3, 5, 6]);
        if (phase !== 1 || !roleMutationSteps.has(sub)) return;

        void refreshProcessInterviews();

        // Step 1.2 : extraction LLM du 1.1 peut finir quelques secondes après la réponse
        if (sub !== 2) return;
        const retryTimer = window.setTimeout(() => {
            void refreshProcessInterviews();
        }, 2500);
        return () => window.clearTimeout(retryTimer);
    }, [effectiveOwnerStep, refreshProcessInterviews]);

    /** Rôles sidebar : process.detected_roles (IA backend) — pas de parsing frontend */
    const detectedRolesForSidebar = React.useMemo(() => {
        const source = detectedRolesFromProcess.length
            ? detectedRolesFromProcess
            : mentionedRoles;
        return filterHiddenRoles(source, hiddenRoles, roleAliases);
    }, [detectedRolesFromProcess, mentionedRoles, roleAliases, hiddenRoles]);

    /** Données sidebar/layout dérivées — évite setState + re-render en cascade */
    const layoutPanelData = React.useMemo(() => {
        const contributorLabel = t('views.processInterview.contributor') || 'Contributor';
        const processOwnerLabel = t('features.swimlane.processOwner') || 'Process Owner';

        const resolveRoleLabel = (role?: string | null, fallbackLabel: string = contributorLabel): string => {
            const trimmed = role?.trim();
            if (!trimmed) return fallbackLabel;
            const normalized = trimmed.toUpperCase();
            if (normalized === 'OWNER' || normalized === 'ADMIN') {
                return processOwnerLabel;
            }
            if (normalized === 'WORKER') {
                return fallbackLabel;
            }
            return formatRoleLabel(trimmed);
        };

        const stakeholderNames = interviewsData.map((interview: iInterview, index: number) => {
            return interview.collaboration?.user?.fullname
                || interview.collaboration?.user?.email
                || `pending-${index}`;
        });
        const avatarColorsByName = assignUniqueAvatarColors(stakeholderNames);

        const interviewStakeholders = interviewsData.map((interview: iInterview, index: number) => {
            const storedRole = interview.role || interview.collaboration?.worker_role || interview.collaboration?.role;
            const rawRole = storedRole
                ? resolveRoleAlias(String(storedRole), roleAliases)
                : storedRole;
            const isProcessOwnerRow = Boolean(interview.for_owner);
            const roleName = isProcessOwnerRow
                ? processOwnerLabel.trim()
                : resolveRoleLabel(rawRole, contributorLabel);
            const userName = interview.collaboration?.user?.fullname || interview.collaboration?.user?.email || 'Invité';
            const interviewWithPending = interview as iInterview & {
                is_pending_invitation?: boolean;
                invitation_token_id?: string;
            };
            const isPendingInvitation =
                interviewWithPending.is_pending_invitation === true || !interview._id;
            const mongoId = interview._id ? String(interview._id) : undefined;
            const uniqueRowId =
                mongoId ||
                (interviewWithPending.invitation_token_id
                    ? String(interviewWithPending.invitation_token_id)
                    : `pending-${interview.collaboration?.user?.email ?? index}`);
            const isDeletable =
                (Boolean(mongoId) || isPendingInvitation) &&
                (!interview.for_owner || (isAdmin && !isProcessOwner));

            const collabUid = interview.collaboration?.user?._id;
            const nameKey = userName.trim().toLowerCase();
            return {
                id: uniqueRowId,
                name: userName,
                role: roleName,
                color: avatarColorsByName.get(nameKey) || getUserColor(userName),
                initials: getUserInitials(userName),
                isPending: isPendingInvitation,
                interviewMongoId: mongoId,
                forOwner: isProcessOwnerRow,
                backendRole: isProcessOwnerRow ? null : (rawRole ?? null),
                invitationTokenId: interviewWithPending.invitation_token_id,
                email: interview.collaboration?.user?.email,
                isDeletable,
                collaborationUserId: collabUid != null ? String(collabUid) : undefined,
            };
        });

        const stakeholders = [...interviewStakeholders];
        
        const displayedInterview = interviewwId
            ? interviewsData.find((i) => i._id && String(i._id) === String(interviewwId)) ?? null
            : null;
        const currentStep = displayedInterview?._id
            ? collabStepsById[String(displayedInterview._id)] ?? null
            : null;
        const discoveryCompleted = isMappingDiscoveryComplete(swimlanes, mappingGenNowMs);

        /** Step owner : live chat → poll API → step phase 1 du fil affiché. */
        const checklistOwnerStep =
            effectiveOwnerStep ??
            (currentStep && currentStep[0] === 1 ? currentStep : null);

        const isOwnerInterview = (() => {
            if (currentStep && currentStep[0] === 2) return false;
            if (currentStep && currentStep[0] === 1) return true;
            if (displayedInterview && typeof displayedInterview === "object" && "for_owner" in displayedInterview) {
                return Boolean(displayedInterview.for_owner);
            }
            return isProcessOwner;
        })();

        const shouldShowCollaboratorChecklist = (() => {
            if (isOwnerInterview) return false;
            if (!isProcessOwner) return true;
            return !isOwnerInterview;
        })();
        
        const progressPercent = shouldShowMappingChecklist && isOwnerInterview && !shouldShowCollaboratorChecklist
            ? getGlobalProgress({ ownerStep: checklistOwnerStep, collaborators: collaboratorsForProgress, discoveryCompleted })
            : 0;

        const progressChecklist = shouldShowMappingChecklist
            ? (isOwnerInterview && !shouldShowCollaboratorChecklist
                ? buildOwnerChecklistItems({
                    ownerStep: checklistOwnerStep,
                    collaborators: collaboratorsForProgress,
                    discoveryCompleted,
                    t
                })
                : buildCollaboratorChecklistItems(currentStep, t))
            : (shouldShowCollaboratorChecklist 
                ? buildCollaboratorChecklistItems(currentStep, t)
                : []);
        
        const contributorStakeholdersOnly = interviewStakeholders.filter((s) => !s.forOwner);
        const totalStakeholders = contributorStakeholdersOnly.length;
        const joinedStakeholders = contributorStakeholdersOnly.filter((s) => !s.isPending).length;
        const completedInterviews = interviewsData.filter((i: iInterview) => !i.for_owner && i.completed_at).length;
        
        const diagrams = getSidebarDiagrams(swimlanes);

        const insights = analyses.map((analysis) => ({
            name: analysis.name || t('words.insight'),
            isNew: false,
        }));

        return {
            stakeholders,
            diagrams,
            insights,
            progressPercent,
            progressChecklist,
            joinedStakeholders,
            completedInterviews,
            totalStakeholders,
        };
    }, [
        analyses,
        formatRoleLabel,
        interviewwId,
        interviewsData,
        effectiveOwnerStep,
        collaboratorsForProgress,
        collabStepsById,
        shouldShowMappingChecklist,
        isProcessOwner,
        isAdmin,
        swimlanes,
        mappingGenNowMs,
        roleAliases,
        t,
    ]);

    /** Ordre sidebar : rôles détectés IA + role_order persisté */
    const sidebarRoleOrder = React.useMemo(() => {
        return pinProcessOwnerRolesFirst(
            buildInviteRoleOptions({
                mentionedRoles: detectedRolesForSidebar,
                extraRoles: roleOrder,
            }).map((r) => r.role),
            processOwnerDisplayLabel
        );
    }, [detectedRolesForSidebar, roleOrder, processOwnerDisplayLabel]);

    const inviteAvailableRoles = React.useMemo(() => {
        const sidebarRoles = layoutPanelData.stakeholders
            .filter((s) => !(s as { forOwner?: boolean }).forOwner)
            .map((s) => (s as { backendRole?: string | null }).backendRole || s.role)
            .filter((r): r is string => typeof r === 'string' && r.trim().length > 0);
        const options = buildInviteRoleOptions({
            interviews: interviewsData.filter((i) => !i.for_owner),
            mentionedRoles: filterHiddenRoles(mentionedRoles, hiddenRoles, roleAliases),
            extraRoles: filterHiddenRoles(
                [
                    ...liveInviteRoles,
                    ...(invited ? [invited] : []),
                    ...sidebarRoles,
                    ...roleOrder,
                ],
                hiddenRoles,
                roleAliases
            ),
        });
        // Sécurité : ne pas proposer un rôle masqué dans le select
        const hiddenKeys = new Set(
            hiddenRoles.map((r) => normalizeInviteRoleKey(r)).filter(Boolean)
        );
        if (hiddenKeys.size === 0) return options;
        return options.filter((o) => !hiddenKeys.has(normalizeInviteRoleKey(o.role)));
    }, [
        interviewsData,
        mentionedRoles,
        liveInviteRoles,
        invited,
        layoutPanelData.stakeholders,
        roleOrder,
        roleAliases,
        hiddenRoles,
    ]);

    // Handler functions for ModernInterviewLayout interactions
    const handleAddStakeholder = async (prefillRole?: string) => {
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernAddContributor);
        // Invitation manuelle depuis la sidebar : ne pas figer le rôle de la question chat
        flushSync(() => {
            setInvitePrefillRole(prefillRole || undefined);
            setInvited(undefined);
        });
        // Recharge synchrone tous les rôles détectés (1.1 + 1.3 + 1.4 + 1.5) avant ouverture
        await loadMentionedRoles();
        triggerModal('#inviteUser');
    };

    React.useEffect(() => {
        const el = document.getElementById('inviteUser');
        if (!el) return;
        const onHidden = () => setInvitePrefillRole(undefined);
        el.addEventListener('hidden.bs.modal', onHidden);
        return () => el.removeEventListener('hidden.bs.modal', onHidden);
    }, []);

    // Handle the role parameter to open the invite user modal (uniquement en étape invitation)
    React.useEffect(() => {
        if (!loading && roleParam && canAddContributor) {
            setInvited(roleParam);
            triggerModal('#inviteUser');
        }
    }, [loading, roleParam, canAddContributor]);

    

    /** Afficher la ligne forçage / compte à rebours aussi pendant cooldown ou génération carte */
    const canForceGenerateMap =
        forceGenStatus.canForceGenerateMap ||
        forceGenStatus.isProcessOwner ||
        isProcessOwner;
    const hasUnfinishedInterviewsLocally =
        layoutPanelData.totalStakeholders > 0 &&
        layoutPanelData.completedInterviews < layoutPanelData.totalStakeholders;
    const effectiveForceGenStatus = {
        ...forceGenStatus,
        allInterviewsCompleted:
            forceGenStatus.allInterviewsCompleted && !hasUnfinishedInterviewsLocally,
        canForceGenerate:
            forceGenStatus.canForceGenerate ||
            (hasUnfinishedInterviewsLocally &&
                canForceGenerateMap &&
                forceGenStatus.cooldownRemainingSeconds === 0 &&
                !forceGenLoading),
    };
    const awaitingFirstBatch =
        isAwaitingFirstBatch(swimlanes, effectiveForceGenStatus.allInterviewsCompleted) ||
        (swimlanes.length === 0 && effectiveForceGenStatus.cooldownRemainingSeconds > 0);
    const mapGenPending =
        hasActivePendingMappings(swimlanes, mappingGenNowMs) || awaitingFirstBatch;
    const mapGenerationTimedOut = shouldShowMappingGenerationTimedOut(
        swimlanes,
        mappingGenNowMs,
    );
    /** Continuer le poll même après le TTL 10 min : la gen peut encore aboutir. */
    const shouldPollSwimlaneMeta =
        hasPendingMappings(swimlanes) || awaitingFirstBatch;
    const mappingGenProgress = getMappingGenerationProgress(swimlanes, mappingGenNowMs);
    const showForceGenSidebar =
        shouldShowMappingChecklist &&
        canForceGenerateMap &&
        !isSampleProcess;

    const handleGenerateDiagram = async () => {
        if (forceGenLoading) return;
        if (isSampleProcess) return;
        if (!canForceGenerateMap) return;
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernForceGenerate);
        setShowForceGenConfirm(true);
    };

    const handleDiagramApprove = async (diagram: any) => {
        const swimlaneId = diagram?.id || diagram?._id;
        if (!swimlaneId) return;
        await trackUiButtonEventAsync(
            UI_BUTTON_EVENTS.interview.modernDiagramApprovalRequest
        );
        setModalWithActionId(String(swimlaneId), 'aproval', '');
    };

    const handleDiagramRename = async (diagram: any) => {
        const swimlaneId = diagram?.id || diagram?._id;
        if (!swimlaneId) return;
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernDiagramRename);
        setModalWithActionId(String(swimlaneId), diagram?.name || '', '');
    };

    const handleDiagramShare = async (diagram: any) => {
        const swimlaneId = diagram?.id || diagram?._id;
        if (!swimlaneId) return;
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernDiagramShare);
        setModalWithActionId(String(swimlaneId), 'share', 'share');
    };

    const handleDiagramDelete = async (diagram: any) => {
        const swimlaneId = diagram?.id || diagram?._id;
        if (!swimlaneId) return;
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernDiagramDelete);
        setModalWithActionId(String(swimlaneId), 'delete', '');
    };

    const handleDiagramClick = async (diagram: any) => {
        const swimlaneId = diagram.id || diagram._id;
        const processIdToUse = processId || currentInterview?.collaboration?.process?._id || id || '';
        if (!swimlaneId || !processIdToUse) return;

        const swimlaneIdStr = String(swimlaneId);
        const knownAccessible = accessibleMapIdsRef.current.has(swimlaneIdStr);

        flushSync(() => {
            setOpeningDiagramId(swimlaneIdStr);
        });
        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernDiagramRowOpen);

        try {
            if (!knownAccessible) {
                const access = await checkMappingOpenAccess(
                    processIdToUse,
                    swimlaneIdStr,
                    users.access_token || '',
                );
                if (!access.allowed) {
                    setInsufficientCreditsInfo(access.denied);
                    return;
                }
            }

            markInterviewDeferredMappingOpen(swimlaneIdStr);
            showMappingLoadingOverlay();
            await waitForOverlayPaint();

        const result = await navigateToMapping({
            processId: processIdToUse,
                mapId: swimlaneIdStr,
            accessToken: users.access_token || '',
                knownAccessible: true,
                skipPrefetchedInstantOpen: true,
                navigate: (path, options) => navigate(path, options),
        });
        if (!result.success) {
                hideMappingLoadingOverlay();
                clearInterviewDeferredMappingOpen();
            setInsufficientCreditsInfo(result.denied);
            }
        } catch {
            hideMappingLoadingOverlay();
            clearInterviewDeferredMappingOpen();
        } finally {
            setOpeningDiagramId(null);
        }
    };

    const handleRunAnalysis = async () => {
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernRunAnalysis);
        clearAnalysisDraft();
        navigate(Routes.COMPANYANALYSIS);
    };

    const handleStartInterview = async () => {
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.railNewDiscovery);
        navigate(getProcessCreatePath(processType));
    };

    const handleOpenLibrary = async () => {
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.railProcessLibrary);
        navigate(Routes.PROCESSLIBRARY);
    };

    const setModalWithActionId = (id: string, action: string, secondAction: string) => {
        // flushSync : le DOM du modal (#generic{id}, #edit{id}, …) doit exister avant triggerModal (id dépend de actionState.actionId).
        flushSync(() => {
            setActionState((prev) => ({
                ...prev,
                actionId: id,
                swimlaneNameOrActionName: action,
                secondAction
            }));
        });

        if (action === 'aproval') {
            triggerModal(`#approval${id}`);
        } else if (action === 'share') {
            triggerModal(`#shareModal${id}`);
        } else if (action === 'delete' || action === 'deleteAnalyse' || action === 'shareAnalyse') {
            triggerModal(`#generic${id}`);
        } else if (action === 'deleInterview') {
            setTimeout(() => triggerModal(`#genericParent${id}`), 0);
        } else if (action === 'renameAnalyse') {
            triggerModal(`#edit${id}`);
        } else {
            triggerModal(`#edit${id}`);
        }
    };

    // const prepareAndShowModal = (col: any) => {
    //     setContributor(col);
    //     if (col?._id) {
    //         triggerModal(`#grantEditPermission${col._id}`);
    //     }
    // };

    const deleteInterviews = async (interviewsId: string, confirmRegenerate = false) => {
        if (!canInviteOrReplaceCollaborators) return;
        const prev = interviewsData;
        const target = prev.find((interview: iInterview) => {
            if (interview._id && String(interview._id) === String(interviewsId)) return true;
            const token = (interview as any).invitation_token_id;
            return token && (`pending-${token}` === interviewsId || String(token) === interviewsId);
        });
        if (!confirmRegenerate) {
        setInterviewsData((list) =>
            list.filter((interview: iInterview) => {
                if (interview._id && String(interview._id) === String(interviewsId)) return false;
                const token = (interview as any).invitation_token_id;
                if (token && (`pending-${token}` === interviewsId || String(token) === interviewsId)) {
                    return false;
                }
                return true;
            })
        );
        }
        try {
            const qs = confirmRegenerate ? '?confirm_regenerate=true' : '';
            const data = await FamlaServices(
                'api_host',
                `/interview/${interviewsId}${qs}`,
                'DELETE',
                '',
                users.access_token
            );
            if (data.body?.status_code === 200 || data.status === 200) {
                if (confirmRegenerate) {
                    setInterviewsData((list) =>
                        list.filter((interview: iInterview) => {
                            if (interview._id && String(interview._id) === String(interviewsId)) return false;
                            const token = (interview as any).invitation_token_id;
                            if (token && (`pending-${token}` === interviewsId || String(token) === interviewsId)) {
                                return false;
                            }
                            return true;
                        })
                    );
                }
                setMappingRegenConfirm(null);
                setAlert({
                    isActive: true,
                    message: t('views.processInterview.sucessDelete'),
                    type: 'success',
                });
            } else {
                setInterviewsData(prev);
                const regen = parseMappingRegenConflict(data);
                if (regen) {
                    setMappingRegenConfirm({
                        interviewId: interviewsId,
                        estimatedCredits: regen.estimatedCredits,
                        isOwnerInterview: Boolean(target?.for_owner),
                    });
                    return;
                }
                const creditsDenied = parseInsufficientCredits(data);
                if (creditsDenied) {
                    setInsufficientCreditsInfo(creditsDenied);
                    return;
                }
                setAlert({
                    isActive: true,
                    message: data.body?.description || t('views.processInterview.youCannotDelete', 'You cannot delete this interview.'),
                    type: 'warning',
                });
            }
        } catch (error) {
            setInterviewsData(prev);
            setLoading(false);
        }

        setTimeout(() => {
            setAlert((prevAlert) => ({ ...prevAlert, isActive: false, message: '' }));
        }, 5000);
    };

    const deleteSwimlaneDiagram = async (swimlaneId: string): Promise<boolean> => {
        const previous = swimlanes;
        setSwimlanes((prev) =>
            prev.filter((swimlane) => String(swimlane._id || swimlane.id) !== String(swimlaneId))
        );
        try {
            const data = await FamlaServices(
                'api_host',
                `/mapping/${swimlaneId}`,
                'DELETE',
                '',
                users.access_token
            );
            if (data.status === 200 || data.body?.status_code === 200) {
                setAlert({
                    isActive: true,
                    type: 'success',
                    message: t('views.processInterview.diagramDeletedSuccess'),
                });
                if (processId) {
                    void refreshSwimlaneMeta(processId);
                }
                // Laisser le toast visible dans le modal avant fermeture
                await new Promise((resolve) => setTimeout(resolve, 1500));
                setAlert((prevAlert) => ({ ...prevAlert, isActive: false, message: '' }));
                return true;
            }
            setSwimlanes(previous);
            setAlert({
                isActive: true,
                type: 'danger',
                message:
                    data.body?.description ||
                    t('views.processInterview.diagramDeletedError'),
            });
            return false;
        } catch (error) {
            setSwimlanes(previous);
            setAlert({
                isActive: true,
                type: 'danger',
                message: t('views.processInterview.diagramDeletedError'),
            });
            setLoading(false);
            return false;
        }
    };

    const deleteAnalyse = async (analyseId: string): Promise<boolean> => {
        const previous = analyses;
        setAnalyses((prev) => prev.filter((analysis) => analysis._id !== analyseId));
        try {
            const data = await FamlaServices(
                'api_host',
                `/report/${analyseId}`,
                'DELETE',
                '',
                users.access_token
            );
            if (data.status === 200 || data.body?.status_code === 200) {
                setAlert({
                    isActive: true,
                    type: 'success',
                    message: t('views.processInterview.analysisDeletedSuccess'),
                });
                await new Promise((resolve) => setTimeout(resolve, 1500));
                setAlert((prevAlert) => ({ ...prevAlert, isActive: false, message: '' }));
                return true;
            }
            setAnalyses(previous);
            setAlert({
                isActive: true,
                type: 'danger',
                message:
                    data.body?.description ||
                    t('views.processInterview.analysisDeletedError'),
            });
            return false;
        } catch (error) {
            setAnalyses(previous);
            setAlert({
                isActive: true,
                type: 'danger',
                message: t('views.processInterview.analysisDeletedError'),
            });
            setLoading(false);
            return false;
        }
    };

    const loadForceGenStatus = React.useCallback(async () => {
        if (!processId || !users.access_token) return;
        try {
            const data = await FamlaServices("api_host", `/mapping/force-status/${processId}`, "GET", "", users.access_token);
            if (data.status === 200 && data.body?.data) {
                const canForceMap = Boolean(
                    data.body.data.can_force_generate_map ?? data.body.data.is_process_owner
                );
                setForceGenStatus({
                    isProcessOwner: Boolean(data.body.data.is_process_owner),
                    canForceGenerateMap: canForceMap,
                    allInterviewsCompleted: Boolean(data.body.data.all_interviews_completed),
                    canForceGenerate: Boolean(data.body.data.can_force_generate),
                    cooldownRemainingSeconds: data.body.data.cooldown_remaining_seconds || 0,
                });
            } else {
                setForceGenStatus({
                    isProcessOwner: false,
                    canForceGenerateMap: false,
                    allInterviewsCompleted: false,
                    canForceGenerate: false,
                    cooldownRemainingSeconds: 0,
                });
            }
        } catch (error) {
            // silent
        }
    }, [processId, users.access_token]);

    React.useEffect(() => {
        loadForceGenStatus();
    }, [loadForceGenStatus]);

    React.useEffect(() => {
        if (forceGenStatus.cooldownRemainingSeconds <= 0) return;
        const interval = setInterval(() => {
            setForceGenStatus((prev) => {
                const newRemaining = prev.cooldownRemainingSeconds - 1;
                if (newRemaining <= 0) {
                    clearInterval(interval);
                    return {
                        ...prev,
                        cooldownRemainingSeconds: 0,
                        canForceGenerate: prev.canForceGenerateMap,
                    };
                }
                return { ...prev, cooldownRemainingSeconds: newRemaining };
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [forceGenStatus.cooldownRemainingSeconds]);

    React.useEffect(() => {
        if (!processId || !shouldPollSwimlaneMeta) return;
        void refreshSwimlaneMeta(processId);
        // Plus espacé après expiration du TTL (pending stale), plus réactif pendant un lot actif.
        const intervalMs = mapGenPending ? 8000 : 12000;
        const intervalId = window.setInterval(() => {
            void refreshSwimlaneMeta(processId);
        }, intervalMs);
        return () => window.clearInterval(intervalId);
    }, [processId, shouldPollSwimlaneMeta, mapGenPending, refreshSwimlaneMeta]);

    React.useEffect(() => {
        // Horloge : lot actif (countdown) OU pending stale (passage TTL → message timeout).
        if (!shouldPollSwimlaneMeta) return;
        setMappingGenNowMs(Date.now());
        const intervalId = window.setInterval(() => setMappingGenNowMs(Date.now()), 1000);
        return () => window.clearInterval(intervalId);
    }, [shouldPollSwimlaneMeta]);

    const mappingGenCountdown = mapGenPending
        ? getMappingGenCountdownSeconds(swimlanes, mappingGenNowMs)
        : null;

    const handleForceGenerate = async () => {
        if (!processId || forceGenLoading) return;
        setForceGenLoading(true);
        setShowForceGenConfirm(false);
        try {
            const data = await FamlaServices("api_host", `/mapping/force/${processId}`, "POST", "", users.access_token);
            if (data.status === 201 || data.body?.status_code === 201) {
                setAlert({ isActive: true, message: t('views.processInterview.forceGenerateSuccess'), type: 'success' });
                await loadForceGenStatus();
                if (processId) {
                    void loadAllSwimlane(processId);
                }
                setForceGenStatus((prev) => {
                    if (prev.cooldownRemainingSeconds > 0) return prev;
                    return {
                        ...prev,
                        cooldownRemainingSeconds: FORCE_MAP_COOLDOWN_FALLBACK_SEC,
                        canForceGenerate: false,
                    };
                });
            } else if (data.status === 429 || data.body?.status_code === 429) {
                const sec = data.body?.data?.cooldown_remaining_seconds;
                if (typeof sec === 'number' && sec > 0) {
                    setForceGenStatus((prev) => ({
                        ...prev,
                        cooldownRemainingSeconds: sec,
                        canForceGenerate: false,
                    }));
                }
                setAlert({ isActive: true, message: t('views.processInterview.forceGenerateCooldown'), type: 'warning' });
                await loadForceGenStatus();
            } else if (data.status === 403 || data.body?.status_code === 403) {
                // Vérifier si c'est une erreur de crédits insuffisants
                const responseType = data.body?.response_type;
                const responseData = data.body?.data;
                
                // Si c'est une erreur "Insufficient credits", afficher le modal dédié
                if (responseType === "Insufficient credits" || 
                    (responseData && typeof responseData === 'object' && 
                     'credits_required' in responseData && 'validity_remaining' in responseData)) {
                    
                    // Extraire les informations de crédits
                    const creditsRequired = responseData?.credits_required || 0;
                    const creditsAvailable = responseData?.validity_remaining || 0;
                    const deficit = creditsRequired - creditsAvailable;
                    const plan = responseData?.plan || "FREE";
                    
                    // Afficher le modal de crédits insuffisants
                    setInsufficientCreditsInfo({
                        creditsRequired,
                        creditsAvailable,
                        deficit,
                        plan
                    });
                } else {
                    // Autre erreur 403 (ex: non-propriétaire)
                    setAlert({ isActive: true, message: data.body?.description || t('views.processInterview.forceGenerateNotOwner'), type: 'warning' });
                }
            } else if (data.status === 400 || data.body?.status_code === 400) {
                setAlert({ isActive: true, message: t('views.processInterview.forceGenerateAlreadyCompleted'), type: 'info' });
            }
        } catch (error) {
            setAlert({ isActive: true, message: 'An error occurred', type: 'danger' });
        }

        setForceGenLoading(false);
        setTimeout(() => {
            setAlert((prev) => ({ ...prev, isActive: false, message: '' }));
        }, 5000);
    };

    if (loading || !isProcessTypeResolved) {
        return (
            <AppLayout>
                <div className="interview-page-viewport flex-grow-1">
                <div className="interview-shell-container flex-grow-1">
                    <div className="interview-shell">
                        <div className="shell iv-skeleton-shell">
                            {/* Left panel skeleton */}
                            <div className="left-panel iv-skel-panel">
                                <div className="lp-header">
                                    <div className="iv-skel-line" style={{ width: '60%', height: 14 }} />
                                </div>
                                <div className="lp-section-scrollable" style={{ padding: '12px 10px' }}>
                                    <div className="iv-skel-label" />
                                    {[80, 65, 75].map((w, i) => (
                                        <div key={i} className="iv-skel-row">
                                            <div className="iv-skel-dot" />
                                            <div className="iv-skel-line" style={{ width: `${w}%` }} />
                                        </div>
                                    ))}
                                    <div className="iv-skel-label" style={{ marginTop: 20 }} />
                                    {[70, 55].map((w, i) => (
                                        <div key={i} className="iv-skel-row">
                                            <div className="iv-skel-dot" style={{ borderRadius: 2 }} />
                                            <div className="iv-skel-line" style={{ width: `${w}%` }} />
                                        </div>
                                    ))}
                                    <div className="iv-skel-label" style={{ marginTop: 20 }} />
                                    {[60, 72, 50].map((w, i) => (
                                        <div key={i} className="iv-skel-row">
                                            <div className="iv-skel-dot" style={{ borderRadius: 2 }} />
                                            <div className="iv-skel-line" style={{ width: `${w}%` }} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Centre skeleton */}
                            <div className="centre iv-skel-centre">
                                <div className="iv-skel-topbar" />
                                <div className="iv-skel-messages">
                                    {/* Message IA */}
                                    <div className="iv-skel-msg iv-skel-msg--left">
                                        <div className="iv-skel-avatar" />
                                        <div className="iv-skel-bubble">
                                            <div className="iv-skel-line" style={{ width: '85%', marginBottom: 6 }} />
                                            <div className="iv-skel-line" style={{ width: '65%' }} />
                                        </div>
                                    </div>
                                    {/* Réponse utilisateur */}
                                    <div className="iv-skel-msg iv-skel-msg--right">
                                        <div className="iv-skel-bubble iv-skel-bubble--user">
                                            <div className="iv-skel-line" style={{ width: '70%' }} />
                                        </div>
                                        <div className="iv-skel-avatar" />
                                    </div>
                                    {/* Message IA */}
                                    <div className="iv-skel-msg iv-skel-msg--left">
                                        <div className="iv-skel-avatar" />
                                        <div className="iv-skel-bubble">
                                            <div className="iv-skel-line" style={{ width: '90%', marginBottom: 6 }} />
                                            <div className="iv-skel-line" style={{ width: '55%', marginBottom: 6 }} />
                                            <div className="iv-skel-line" style={{ width: '75%' }} />
                                        </div>
                                    </div>
                                    {/* Réponse utilisateur */}
                                    <div className="iv-skel-msg iv-skel-msg--right">
                                        <div className="iv-skel-bubble iv-skel-bubble--user">
                                            <div className="iv-skel-line" style={{ width: '50%' }} />
                                        </div>
                                        <div className="iv-skel-avatar" />
                                    </div>
                                </div>
                                <div className="iv-skel-inputbar">
                                    <div className="iv-skel-input-inner">
                                        <div className="iv-skel-line" style={{ width: '100%', height: 18 }} />
                                    </div>
                                </div>
                            </div>

                            {/* Right panel skeleton */}
                            <div className="right-panel iv-skel-panel">
                                <div className="rp-scroll" style={{ padding: 10 }}>
                                    <div className="iv-skel-label" />
                                    <div className="iv-skel-progbar" />
                                    {[75, 60, 80, 55, 70].map((w, i) => (
                                        <div key={i} className="iv-skel-row">
                                            <div className="iv-skel-dot" />
                                            <div className="iv-skel-line" style={{ width: `${w}%` }} />
                                        </div>
                                    ))}
                                    <div className="iv-skel-label" style={{ marginTop: 20 }} />
                                    {[65, 80, 55].map((w, i) => (
                                        <div key={i} className="iv-skel-row">
                                            <div className="iv-skel-dot iv-skel-dot--sm" />
                                            <div className="iv-skel-line" style={{ width: `${w}%` }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div className="interview-page-viewport flex-grow-1">
            <ModernInterviewLayout
                processName={processName}
                stakeholders={layoutPanelData.stakeholders}
                diagrams={layoutPanelData.diagrams}
                insights={layoutPanelData.insights}
                progressPercent={layoutPanelData.progressPercent}
                progressChecklist={layoutPanelData.progressChecklist}
                joinedStakeholders={layoutPanelData.joinedStakeholders}
                completedInterviews={layoutPanelData.completedInterviews}
                totalStakeholders={layoutPanelData.totalStakeholders}
                onCollaboratorMenuReplace={canInviteOrReplaceCollaborators ? handleReplaceMenber : undefined}
                onCollaboratorMenuDelete={
                    canInviteOrReplaceCollaborators
                        ? async (interviewMongoId) => {
                              await trackUiButtonEventAsync(
                                  UI_BUTTON_EVENTS.interview.modernContributorDelete
                              );
                              setModalWithActionId(interviewMongoId, 'deleInterview', '');
                          }
                        : undefined
                }
                roleOrder={sidebarRoleOrder}
                onRenameRole={
                    canInviteOrReplaceCollaborators
                        ? (label) => setRoleFormModal({ mode: 'rename', initialLabel: label })
                        : undefined
                }
                onCreateRole={
                    canInviteOrReplaceCollaborators
                        ? () => setRoleFormModal({ mode: 'create' })
                        : undefined
                }
                onDeleteRole={canInviteOrReplaceCollaborators ? openDeleteRoleModal : undefined}
                onReorderRoles={canInviteOrReplaceCollaborators ? handleReorderRoles : undefined}
                onMovePerson={canInviteOrReplaceCollaborators ? handleMovePerson : undefined}
                onChangePersonRole={
                    canInviteOrReplaceCollaborators
                        ? (memberId, currentRole, personName) =>
                              setChangeRoleModal({ memberId, currentRole, personName })
                        : undefined
                }
                onInviteToRole={
                    canInviteOrReplaceCollaborators
                        ? (roleLabel) => {
                              void handleAddStakeholder(resolveStoredRoleLabel(roleLabel));
                          }
                        : undefined
                }
                canManageProcessCollaborators={canInviteOrReplaceCollaborators}
                onBack={async () => {
                    await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.modernBack);
                    navigate(Routes.HOMEVIEW);
                }}
                onAddStakeholder={
                    canAddContributor
                        ? () => {
                              void handleAddStakeholder();
                          }
                        : undefined
                }
                onGenerateDiagram={handleGenerateDiagram}
                showForceGenSidebar={showForceGenSidebar}
                mapGenerationPending={mapGenPending}
                mapGenerationTimedOut={mapGenerationTimedOut}
                mappingGenProgress={mappingGenProgress}
                mappingGenCountdown={mappingGenCountdown}
                diagramsLoading={diagramsLoading}
                openingDiagramId={openingDiagramId}
                documentMapBuildCountdown={documentMapBuildCountdown}
                forceGenStatus={effectiveForceGenStatus}
                forceGenLoading={forceGenLoading}
                onDiagramClick={handleDiagramClick}
                onDiagramApprove={handleDiagramApprove}
                onDiagramRename={handleDiagramRename}
                onDiagramShare={handleDiagramShare}
                onDiagramDelete={handleDiagramDelete}
                onRunAnalysis={handleRunAnalysis}
                onStartInterview={handleStartInterview}
                onOpenLibrary={handleOpenLibrary}
                onExportTranscript={async () => {
                    await trackUiButtonEventAsync(
                        UI_BUTTON_EVENTS.interview.railExportTranscript
                    );
                    try {
                        if (!processId) return;
                        const response = await FamlaServices("api_host", `/file/export-pdf/?process_id=${processId}`, "GET", "", users.access_token);
                        if (response.contentType && response.contentType.includes("application/pdf")) {
                            const blob = response.body;
                            const link = document.createElement('a');
                            link.href = window.URL.createObjectURL(blob);
                            link.download = `process_${id}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                    } catch (e) {
                        // silent
                    }
                }}
                onShare={async () => {
                    await trackUiButtonEventAsync(UI_BUTTON_EVENTS.interview.railShare);
                    const targetSwimlaneId = swimlanes?.[0]?._id || swimlanes?.[0]?.id;
                    if (targetSwimlaneId) {
                        setModalWithActionId(targetSwimlaneId, 'share', 'share');
                        return;
                    }
                    if (canInviteOrReplaceCollaborators) {
                        triggerModal('#inviteUser');
                    }
                }}
                activeInterviewId={interviewwId}
                onSelectContributorInterview={
                    canSelectContributorInterview ? handleSelectContributorInterview : undefined
                }
                processType={processType}
            >
                <ChatBot
                    time={time}
                    interviewwId={interviewwId}
                    id={id}
                    setInvited={setInvited}
                    swimlanes={swimlanes}
                    collaborator={currentInterview?.collaboration?.user || users.user}
                    procesId={currentInterview?.collaboration?.process?._id || id || ''}
                    interviews={currentInterview || ({} as iInterview)}
                    processType={processType}
                    isProcessTypeResolved={isProcessTypeResolved}
                    collaborationInterviews={interviewsData}
                    useModernLayout={true}
                    canInviteCollaborators={canInviteOrReplaceCollaborators}
                    onProcessMembersRefresh={processId ? refreshProcessInterviews : undefined}
                    onMapBuildCountdownChange={setDocumentMapBuildCountdown}
                    onOwnerStepChange={setLiveOwnerStep}
                    onInviteRolesChange={setLiveInviteRoles}
                    inviteYesSenderRef={inviteYesSenderRef}
                />
            </ModernInterviewLayout>
            </div>
            <InviteUser
                processId={processId || currentInterview?.collaboration?.process?._id || ''}
                closeRef={closeRef}
                interviewwId={interviewwId}
                invited={invitePrefillRole || invited}
                availableRoles={inviteAvailableRoles}
                onInviteSuccess={async () => {
                    setInvitePrefillRole(undefined);
                    await inviteYesSenderRef.current?.();
                    await refreshProcessInterviews();
                }}
            />
            {processId && (actionState.actionId || swimlanes?.[0]?._id) && (
                <ShareModal
                    swimlaneId={actionState.actionId || swimlanes[0]._id}
                    processId={processId}
                    linkBasePath={actionState.swimlaneNameOrActionName === 'shareAnalyse'
                        ? `${actionState.actionId}`
                        : `${processId}/${actionState.actionId || swimlanes[0]._id}`}
                />
            )}
            <ReplaceModal
                processId={processId}
                closeRef={closeRef}
                interviewwId={replace.replaceId}
                invited={replace.replaceRole}
                availableRoles={[
                    ...interviewsData.map((iv) => ({ role: iv.role })),
                    ...roleOrder.map((role) => ({ role })),
                    ...mentionedRoles.map((role) => ({ role })),
                ]}
                onReplaceSuccess={async () => {
                    await refreshProcessInterviews();
                }}
            />
            <ReminderModal
                closeRef={reminderCloseRef}
                interviewId={replace.replaceId}
            />
            <ApprovalModal
                swimlaneId={actionState.actionId}
                isDarkMode={true}
            />
            <EditModal
                swimlaneId={actionState.actionId}
                processId={processId}
                setElement={actionState.secondAction === 'renameAnalyse' ? setAnalyses : setSwimlanes}
                processName={actionState.swimlaneNameOrActionName}
                inputLabel={actionState.secondAction === 'renameAnalyse'
                    ? t('features.swimlane.analysisName')
                    : t('navigation.diagramName')}
                data={actionState.secondAction === 'renameAnalyse' ? analyses : swimlanes}
                secondAction={actionState.secondAction}
            />
            <GenericModal
                sentence={actionState.swimlaneNameOrActionName === 'deleteAnalyse'
                    ? t('views.processInterview.confirmDeleteAnalysisReport')
                    : t('views.processInterview.deleteDiagramme')}
                processId={actionState.actionId}
                title={t('navigation.deleteConfirmation')}
                action={actionState.swimlaneNameOrActionName === 'deleteAnalyse'
                    ? (id: string) => deleteAnalyse(id)
                    : (id: string) => deleteSwimlaneDiagram(id)}
                actionButton={t('words.delete')}
                alert={alert}
            />
            <GenericModalParent
                sentence={
                    interviewsData.find((iv) => String(iv._id) === String(actionState.actionId))?.for_owner
                        ? t('views.processInterview.deleteOwnerInterviewQuestion')
                        : t('views.processInterview.deleteInterviewQuestion')
                }
                processId={
                    actionState.swimlaneNameOrActionName === 'deleInterview'
                        ? actionState.actionId
                        : actionState.interviewId
                }
                title={t('navigation.deleteConfirmation')}
                action={() => { deleteInterviews(actionState.actionId); }}
                actionButton={t('words.delete')}
                isActiveAlert={alert.isActive}
                alertMsg={alert.message}
                alertType={alert.type}
            />
            <RoleFormModal
                mode={roleFormModal?.mode || 'create'}
                initialLabel={roleFormModal?.initialLabel || ''}
                isOpen={Boolean(roleFormModal)}
                onClose={() => setRoleFormModal(null)}
                onSubmit={async (label, previousLabel) => {
                    if (roleFormModal?.mode === 'rename') {
                        const ok = await handleRenameRole(
                            previousLabel || roleFormModal.initialLabel || '',
                            label
                        );
                        return ok !== false;
                    }
                    const ok = await handleCreateRole(label);
                    return ok !== false;
                }}
            />
            <ChangePersonRoleModal
                personName={changeRoleModal?.personName}
                currentRole={changeRoleModal?.currentRole}
                availableRoles={[
                    ...interviewsData.map((iv) => ({ role: iv.role })),
                    ...roleOrder.map((role) => ({ role })),
                    ...mentionedRoles.map((role) => ({ role })),
                ]}
                isOpen={Boolean(changeRoleModal)}
                onClose={() => setChangeRoleModal(null)}
                onSubmit={async (role) => {
                    if (!changeRoleModal) return false;
                    await handleMovePerson(changeRoleModal.memberId, role);
                    return true;
                }}
            />
            <DeleteRoleModal
                roleLabel={deleteRoleState?.label || ''}
                availableRoles={[
                    ...roleOrder,
                    ...interviewsData.map((iv) => iv.role),
                    ...mentionedRoles,
                ]}
                isOpen={Boolean(deleteRoleState)}
                onClose={() => {
                    setDeleteRoleState(null);
                    setRoleDeleteNeedsRegen(null);
                }}
                onConfirm={confirmDeleteRole}
                mappingRegenHint={roleDeleteNeedsRegen}
                hasAssignees={deleteRoleHasAssignees}
            />
            <GrantEditPermissionModal
                stackholder={contributor}
                setInterviews={setInterviewsData}
                processId={processId}
                interviews={interviewsData}
            />
            {insufficientCreditsInfo && (
                <InsufficientCreditsModal
                    id="insufficientCreditsModal"
                    creditsRequired={insufficientCreditsInfo.creditsRequired}
                    creditsAvailable={insufficientCreditsInfo.creditsAvailable}
                    deficit={insufficientCreditsInfo.deficit}
                    plan={insufficientCreditsInfo.plan}
                    onClose={() => setInsufficientCreditsInfo(null)}
                    isOwner={isOwner}
                    adminEmail={ownerEmail}
                />
            )}
            {mappingRegenConfirm && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content bg-dark">
                            <div className="modal-header border-0">
                                <h5 className="modal-title text-white">
                                    {t('views.processInterview.deleteMappingSourceTitle')}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close bg-white"
                                    onClick={() => setMappingRegenConfirm(null)}
                                    aria-label={t('words.close')}
                                />
                            </div>
                            <div className="modal-body">
                                {mappingRegenConfirm.isOwnerInterview ? (
                                    <p className="text-theme">
                                        {t('views.processInterview.deleteOwnerInterviewQuestion')}
                                    </p>
                                ) : null}
                                <p className="text-theme mb-0">
                                    {t('views.processInterview.deleteMappingSourceWarning', {
                                        credits: mappingRegenConfirm.estimatedCredits,
                                    })}
                                </p>
                            </div>
                            <div className="modal-footer border-secondary">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setMappingRegenConfirm(null)}
                                >
                                    {t('words.cancel')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={mappingRegenLoading}
                                    onClick={async () => {
                                        setMappingRegenLoading(true);
                                        await deleteInterviews(mappingRegenConfirm.interviewId, true);
                                        setMappingRegenLoading(false);
                                    }}
                                >
                                    {mappingRegenLoading ? (
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    ) : null}
                                    {t('views.processInterview.deleteAndRegenerate')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showForceGenConfirm && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content force-map-gen-modal">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fa-solid fa-bolt me-2 tenant-accent-icon" aria-hidden />
                                    {t('views.processInterview.forceGenerateConfirmTitle')}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowForceGenConfirm(false)}
                                    aria-label={t('words.close')}
                                />
                            </div>
                            <div className="modal-body">
                                <p>{t('views.processInterview.forceGenerateConfirmMessage')}</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowForceGenConfirm(false)}>
                                    {t('words.cancel')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-theme text-white"
                                    onClick={handleForceGenerate}
                                    disabled={forceGenLoading}
                                >
                                    {forceGenLoading && (
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    )}
                                    {t('words.confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
};

ModernInterviewView.displayName = 'ModernInterviewView';

export default ModernInterviewView;
